/**
 * Builds the Signify dropdown menu items for `ArTableControls`.
 *
 * `configDisabledReason` disables the two mapping-dependent items and is folded
 * into their `label`, never a `Tooltip`: antd sets `pointer-events: none` on a
 * disabled menu item, so a tooltip attached to one never fires. The two workflow
 * items (`signify-rfq`, `signify-order`) do not read the mappings and stay live.
 *
 * `signify-insert-list` takes its label as a node from the caller instead of building one here: the
 * label carries the live count of the fetch in progress, and only a component can subscribe to it.
 * That item's `configDisabledReason` suffix therefore belongs to the label's owner too.
 */
import type { ReactNode } from 'react';
import type { MenuProps } from 'antd';
import { PlusOutlined, ShoppingCartOutlined, ToolOutlined, UnorderedListOutlined } from '@ant-design/icons';
import type { Workflow } from '#types';

type BuildSignifyApiMenuItemsParams = {
  disabled: boolean;
  configDisabledReason: string | null;
  hasActiveSession: boolean;
  rfqWorkflow: Workflow | null;
  isRfqRunning: boolean;
  orderWorkflow: Workflow | null;
  isOrderRunning: boolean;
  bomIsEmpty: boolean;
  /** Live label of the parts-list item, count and disabled reason included. */
  partsListLabel: ReactNode;
  onAddFromSignify: () => void;
  onAddPartsListFromSignify: () => void;
  onConfigureSignify: () => void;
  onRunRfq: () => void;
  onRunOrder: () => void;
};

export function buildSignifyApiMenuItems(
  params: BuildSignifyApiMenuItemsParams
): NonNullable<MenuProps['items']> {
  const {
    disabled,
    configDisabledReason,
    hasActiveSession,
    rfqWorkflow,
    isRfqRunning,
    orderWorkflow,
    isOrderRunning,
    bomIsEmpty,
    partsListLabel,
    onAddFromSignify,
    onAddPartsListFromSignify,
    onConfigureSignify,
    onRunRfq,
    onRunOrder,
  } = params;

  const withReason = (label: string): string =>
    configDisabledReason ? `${label} — ${configDisabledReason}` : label;

  const items: NonNullable<MenuProps['items']> = [
    {
      key: 'signify-insert',
      label: withReason('Add From Signify'),
      icon: <PlusOutlined />,
      disabled: disabled || !!configDisabledReason,
      onClick: onAddFromSignify,
    },
    {
      key: 'signify-insert-list',
      label: partsListLabel,
      icon: <UnorderedListOutlined />,
      disabled: disabled || !!configDisabledReason,
      onClick: onAddPartsListFromSignify,
    },
    {
      key: 'signify-configure',
      label: withReason(hasActiveSession ? 'Resume Signify Configuration' : 'Configure with Signify'),
      icon: <ToolOutlined />,
      disabled: disabled || !!configDisabledReason,
      onClick: onConfigureSignify,
    },
  ];

  if (rfqWorkflow) {
    items.push({
      key: 'signify-rfq',
      label: rfqWorkflow.description?.shortLabel ?? 'Create/Revise Quote',
      icon: <ToolOutlined />,
      disabled: disabled || isRfqRunning,
      onClick: onRunRfq,
    });
  }

  if (orderWorkflow) {
    items.push({
      key: 'signify-order',
      label: orderWorkflow.description?.shortLabel ?? 'Submit Order',
      icon: <ShoppingCartOutlined />,
      disabled: disabled || isOrderRunning || bomIsEmpty,
      onClick: onRunOrder,
    });
  }

  return items;
}
